import { defineStore } from 'pinia'
import { useAuthStore } from '@/store/pinia/auth'
import { useOrganizationStore } from '@/store/pinia/organization'
import { useNavConfigStore } from '@/store/pinia/organization/navConfig'
import { useOidcMappingsStore } from '@/store/pinia/organization/oidcMappings'
import { makeResetStoreAction, createPersistedState, VERSION_KEY } from '@/utils/helpers/pinia'
import { useRootStore } from './root'

const VERSION = 1

export const state = () => ({
  [VERSION_KEY]: VERSION,
  currentScopeDepth: 0,
  scopes: {
    0: {
      canonical: '',
      jwt: null,
    },
  },
})

export const useCustomersStore = defineStore('customers', {
  state,

  getters: {
    getParentList: (state) => {
      const orgsArray = Object.values(state.scopes)
      return _.without(orgsArray, orgsArray.at(-1))
    },
    isInCustomerScope: (state) => state.currentScopeDepth !== 0,
    currentScope: (state) => ({
      ...state.scopes[state.currentScopeDepth],
    }),
    currentOrgScope: (state) => {
      const orgStore = useOrganizationStore()
      const authStore = useAuthStore()

      return {
        canonical: orgStore.orgCanonical,
        jwt: authStore.jwt,
        name: orgStore.orgName,
        fullPath: Object.values(state.scopes).map(({ name }) => _.capitalize(name)).join(' / '),
      }
    },
  },

  actions: {
    RESET_CUSTOMERS_STATE: makeResetStoreAction(state),

    async LOGIN_AS_CUSTOMER ({ canonical, name }) {
      const switched = await this.CHANGE_ORG_SCOPE({ nextCanonical: canonical })
      if (!switched) return
      this.INCREMENT_SCOPE_DEPTH()
      const authStore = useAuthStore()
      this.SET_CURRENT_SCOPE({ canonical, name, jwt: authStore.jwt })
    },

    async LOGIN_AS_PARENT ({ canonical }) {
      const scopeDepth = _.findKey(this.scopes, ['canonical', canonical])
      const switched = await this.CHANGE_ORG_SCOPE({ nextCanonical: canonical })
      if (!switched) return
      this.SET_SCOPE_DEPTH({ scopeDepth })
      this.REMOVE_CUSTOMER_SCOPES()
    },

    async LOGIN_AS_HIGHER_ORG ({ canonical, scopeDepth, name }) {
      const switched = await this.CHANGE_ORG_SCOPE({ nextCanonical: canonical })
      if (!switched) return
      this.SET_SCOPE_DEPTH({ scopeDepth })
      const authStore = useAuthStore()
      this.SET_CURRENT_SCOPE({ canonical, name, jwt: authStore.jwt })
      this.REMOVE_CUSTOMER_SCOPES()
    },

    async LOGIN_AS_SIBLING ({ canonical, name }) {
      const switched = await this.CHANGE_ORG_SCOPE({ nextCanonical: canonical })
      if (!switched) return
      const authStore = useAuthStore()
      this.SET_CURRENT_SCOPE({ canonical, name, jwt: authStore.jwt })
    },

    async GET_ANCESTORS ({ orgCanonical }) {
      const { data: ancestors = [] } = (await this.$cycloid.ydAPI.getOrgAncestors(orgCanonical)) || {}

      if (ancestors.length > 1) {
        const [{ canonical: prevCanonical }] = ancestors
        await this.CHANGE_ORG_SCOPE({ prevCanonical, nextCanonical: orgCanonical })
        this.RECREATE_CUSTOMERS(ancestors)
      }
    },

    async CHANGE_ORG_SCOPE (args) {
      const authStore = useAuthStore()
      const switchPromise = this._performOrgScopeSwitch(args)
      // Register the in-flight scope switch so a concurrent no-arg REFRESH_TOKEN (the auto-refresh
      // interceptor) coalesces into it instead of refreshing for the stale scope and reverting the
      // switch mid-flight. This mirrors the flip-flop guard CHANGE_ORGANIZATION got in ENG-198 —
      // the child-scope path was left uncovered, which is part of ENG-220.
      authStore.SET_ORG_SWITCH_IN_FLIGHT(switchPromise)
      try {
        return await switchPromise
      } finally {
        if (authStore._orgSwitchInFlight === switchPromise) authStore.SET_ORG_SWITCH_IN_FLIGHT(null)
      }
    },

    async _performOrgScopeSwitch ({ prevCanonical, nextCanonical }) {
      const authStore = useAuthStore()

      // scopes[0] holds the root org once login has seeded it (ENG-220). A fresh browser tab never
      // gets that seed — customersStore is per-tab (ENGFE-512) — so scopes[0] is empty and this
      // omits organization_canonical entirely, letting the BE resolve the parent from the user's own
      // identity: ResolveParentForChild walks nextCanonical's real ancestor chain looking for one the
      // user is a direct member of, independent of whatever org the token currently happens to be
      // scoped to. This fixes the fresh-tab repro (ENG-307), but scopes[0] can still be non-empty and
      // stale on a tab that's already been scoped into one child org and then navigates cross-branch
      // by URL — that case still sends the wrong parent and isn't handled here.
      const parentCanonical = prevCanonical || this.scopes[0]?.canonical
      const query = {
        ...(parentCanonical ? { organization_canonical: parentCanonical } : {}),
        ...(nextCanonical ? { child_canonical: nextCanonical } : {}),
      }
      if (_.isEmpty(query)) return false

      const refreshTokenError = await authStore.REFRESH_TOKEN({ query })

      if (refreshTokenError === 'MFAError') return false
      const jwt = authStore.jwt
      if (!jwt) return false

      // Kick the nav config in parallel, fire-and-forget (see _performOrgSwitch / ENG-361).
      useNavConfigStore().FETCH_NAV_CONFIG({ orgCanonical: nextCanonical })

      const rootStore = useRootStore()
      await rootStore.UPDATE_USER_SESSION(jwt)

      const orgStore = useOrganizationStore()
      orgStore.RESET_ORG_STATE('errors')
      orgStore.RESET_ORG_STATE('available')
      // Reset OIDC group mappings/settings like the top-level switch does (organization/index.js):
      // they are per-organization, so entering a child scope must not keep the previous org's (ENG-360).
      useOidcMappingsStore().RESET_OIDC_MAPPINGS_STATE()
      await orgStore.GET_ORGANIZATION(nextCanonical)
      // Re-point nav at the org we actually landed on if the switch failed to advance detail (ENG-403).
      if (orgStore.orgCanonical !== nextCanonical) useNavConfigStore().FETCH_NAV_CONFIG({ orgCanonical: orgStore.orgCanonical })
      return true
    },

    RECREATE_CUSTOMERS (ancestors) {
      const scopes = Object.fromEntries(ancestors.map(({ canonical }, i) => [i, { canonical, jwt: null }]))

      this.scopes = scopes
      this.currentScopeDepth = ancestors.length - 1
    },

    SET_CURRENT_SCOPE (scope) {
      const alreadyExists = _.values(this.scopes).some(({ canonical, jwt }) => _.isEqual(scope.canonical, canonical) && _.isEqual(scope.jwt, jwt))
      if (!alreadyExists) this.scopes[this.currentScopeDepth] = scope
    },

    INCREMENT_SCOPE_DEPTH () {
      this.currentScopeDepth++
    },

    SET_SCOPE_DEPTH ({ scopeDepth = 0 } = {}) {
      if (isNaN(Number(scopeDepth))) throw new Error('[SET_SCOPE_DEPTH]: was passed a scopeDepth that is not a Number', scopeDepth)
      this.currentScopeDepth = Number(scopeDepth)
    },

    REMOVE_CUSTOMER_SCOPES () {
      for (const key in this.scopes) {
        if (Number(key) > this.currentScopeDepth && Number(key) !== 0) delete this.scopes[key]
      }
    },
  },
  // Persist per-tab (ENGFE-512): the scope chain (incl. per-scope JWTs) must not leak across tabs,
  // otherwise a reloaded tab could rehydrate another tab's scope and re-scope its token to the wrong
  // org. A fresh tab rebuilds its scope from the URL via the `checkOrgOrigin` router guard.
  persistedState: createPersistedState('cyCustomers', { version: VERSION, storage: sessionStorage }),
})
